import { ref, reactive, nextTick } from 'vue';

export default function useConfirmation() {
  // Use ref for simple boolean values
  const confirmationOpen = ref(false);
  // Use reactive for complex objects that need property mutations
  const confirmationConfig = reactive({
    title: 'Confirmação',
    message: 'Tem certeza que deseja realizar esta ação?',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    confirmColor: 'primary',
    loading: false,
    // Add performance optimization settings
    noTransition: false, // Skip transitions for faster display
  });
  
  // Promise and resolver references
  let resolveConfirmation = null;
  let rejectConfirmation = null;
  
  // More efficient reset method to prepare config object
  const resetConfig = () => {
    // Reset only the properties that changed
    Object.assign(confirmationConfig, {
      title: 'Confirmação',
      message: 'Tem certeza que deseja realizar esta ação?',
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      confirmColor: 'primary',
      loading: false,
      noTransition: false,
    });
  };

  const setLoading = (isLoading) => {
    confirmationConfig.loading = isLoading;
  };

  // Pre-configuration for faster display
  const presetConfirmationConfig = (config = {}) => {
    // Apply all config properties in one operation for better performance
    Object.assign(confirmationConfig, {
      title: config.title || 'Confirmação',
      message: config.message || 'Tem certeza que deseja realizar esta ação?',
      confirmText: config.confirmText || 'Confirmar', 
      cancelText: config.cancelText || 'Cancelar',
      confirmColor: config.confirmColor || 'primary',
      noTransition: config.noTransition || false,
    });
  };
  
  // Optimized confirm method that prevents unnecessary re-renders
  const confirm = async (config = {}) => {
    // First update config (before opening modal)
    presetConfirmationConfig(config);
    
    // Then create promise
    const promise = new Promise((resolve, reject) => {
      resolveConfirmation = resolve;
      rejectConfirmation = reject;
    });
    
    // Configure for speed if it's urgent
    if (config.urgent) {
      confirmationConfig.noTransition = true;
    }
    
    // Wait for DOM update before showing modal
    await nextTick();
    
    // Finally open modal
    confirmationOpen.value = true;
    
    return promise;
  };

  const handleConfirm = () => {
    confirmationOpen.value = false;
    if (resolveConfirmation) {
      resolveConfirmation(true);
      resolveConfirmation = null;
      rejectConfirmation = null;
    }
    resetConfig();
  };

  const handleCancel = () => {
    confirmationOpen.value = false;
    if (rejectConfirmation) {
      rejectConfirmation('User cancelled');
      resolveConfirmation = null;
      rejectConfirmation = null;
    }
    resetConfig();
  };

  return {
    confirmationOpen,
    confirmationConfig,
    confirm,
    handleConfirm,
    handleCancel,
    setLoading,
    
    // New helper methods for direct configuration
    presetConfirmationConfig,
  };
}
